import { getAuth, signInWithPopup, FacebookAuthProvider as FirebaseFacebookAuthProvider, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { AuthProvider, AuthUser, SocialAuthConfig } from './types';

export class FacebookAuthService implements AuthProvider {
  private auth = getAuth();

  private provider: FirebaseFacebookAuthProvider;
  
  private config: SocialAuthConfig;

  constructor(config: SocialAuthConfig) {
    this.config = config;
    this.provider = new FirebaseFacebookAuthProvider();
    
    // Firebase adds 'email' scope by default, so we need to override it completely
    // Clear any default scopes and only add what we want
    this.provider.addScope("email");            // <-- request email
    this.provider.addScope("public_profile");   // optional; usually implied
    this.provider.setCustomParameters({ display: "popup" });
    
    console.log('Facebook Auth: Provider configured with custom scope override');
  }

  async signIn(): Promise<AuthUser | null> {
    try {
      const result = await signInWithPopup(this.auth, this.provider);
      const token = await result.user.getIdToken();
      return this.mapFirebaseUser(result.user, token, 'facebook');
    } catch (error) {
      console.error('Facebook sign-in failed:', error);
      throw new Error('Facebook sign-in failed');
    }
  }

  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(this.auth);
    } catch (error) {
      console.error('Facebook sign-out failed:', error);
      throw new Error('Facebook sign-out failed');
    }
  }

  getCurrentUser(): AuthUser | null {
    const user = this.auth.currentUser;
    if (!user) return null;
    
    return this.mapFirebaseUser(user, '', 'facebook');
  }

  async getIdToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return null;
    
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(this.auth, (firebaseUser) => {
      if (firebaseUser) {
        const user = this.mapFirebaseUser(firebaseUser, '', 'facebook');
        callback(user);
      } else {
        callback(null);
      }
    });
  }

  private mapFirebaseUser(firebaseUser: any, token: string, provider: string): AuthUser {
    const providerData = firebaseUser.providerData?.find((p: any) => p.providerId === 'facebook.com');
    
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      accessToken: token || firebaseUser.accessToken || '',
      provider,
      providerData: providerData ? {
        providerId: providerData.providerId,
        uid: providerData.uid,
        displayName: providerData.displayName,
        email: providerData.email,
        photoURL: providerData.photoURL
      } : undefined
    };
  }
} 